import type { StaffNotification, StaffNotificationList } from '@erp/shared';
import type { TFunction } from 'i18next';
import {
  Bell, BellOff, BellRing, Check, Receipt, ShoppingCart, Store, Target, TicketX, Truck, Undo2, UserPlus, UsersRound, Wallet,
} from 'lucide-react';
import { type ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/shadcn/index.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { formatDateTime, formatRelative } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { isNotifySoundMuted, playNotificationSound, setNotifySoundMuted } from '../../lib/sounds.js';

/**
 * The header bell: unread badge + dropdown of recent business events (new
 * online orders first and foremost), polled every ~25s while the tab is
 * visible. Rows arrive LEAN from the API (eventType + facts) and are rendered
 * through i18n here, so Arabic and English both read natively. When the unread
 * count RISES while the panel is open we toast the newest item and play a
 * soft chime (muteable, persisted).
 */

const POLL_MS = 25_000;

type Icon = ComponentType<{ className?: string }>;

// Exported: the dashboard's notifications card renders the SAME rows with the
// SAME words — one vocabulary for an event, wherever it appears.
export const EVENT_ICONS: Record<string, Icon> = {
  'store.order.placed': ShoppingCart,
  'pos.sale.finalized': Store,
  'store.order.counter': Store,
  'invoice.posted': Receipt,
  'payment.received': Wallet,
  'delivery.delivered': Truck,
  'hr.payroll.posted': UsersRound,
  'crm.lead.converted': Target,
  'coupon.offline_unverified': TicketX,
  'customer.type_requested': UserPlus,
  'fib.order_paid': Wallet,
};

export function titleOf(t: TFunction, n: StaffNotification): string {
  switch (n.eventType) {
    case 'store.order.placed': return t('notifications.event.orderPlaced');
    case 'pos.sale.finalized': return t('notifications.event.posSale');
    case 'store.order.counter': return t('notifications.event.counterSale');
    case 'invoice.posted':
      return n.payload.isCreditNote
        ? t('notifications.event.creditNotePosted')
        : t('notifications.event.invoicePosted');
    case 'payment.received': return t('notifications.event.paymentReceived');
    case 'delivery.delivered': return t('notifications.event.delivered');
    case 'hr.payroll.posted': return t('notifications.event.payrollPosted');
    case 'crm.lead.converted': return t('notifications.event.leadConverted');
    case 'customer.type_requested': return t('notifications.event.customerTypeRequested');
    case 'fib.order_paid': return t('notifications.event.fibOrderPaid');
    case 'coupon.offline_unverified':
      return n.payload.couponReason === 'coupon_amount_drift'
        ? t('notifications.event.couponAmountDrift')
        : t('notifications.event.couponUnverified');
    default: return n.eventType;
  }
}

export function bodyOf(t: TFunction, locale: string, n: StaffNotification): string {
  if (n.eventType === 'crm.lead.converted') return t('notifications.event.leadConvertedBody');
  const p = n.payload;
  if (n.eventType === 'coupon.offline_unverified') {
    // Name the code, the order, the cashier and the money — everything needed
    // to chase it without opening anything.
    const money = p.amount && p.currencyCode ? formatMoney(p.amount, { locale, currency: p.currencyCode }) : '';
    return [
      [p.couponCode, p.docNumber].filter(Boolean).join(' · '),
      money ? t('notifications.event.couponHonoured', { amount: money }) : '',
      p.repName,
    ].filter(Boolean).join(' — ');
  }
  if (n.eventType === 'customer.type_requested') {
    // Who, what they claim to be, and how to reach them — enough to decide
    // without opening anything.
    return [p.partyName, p.requestedTypeName, p.partyPhone].filter(Boolean).join(' · ');
  }
  const bits: string[] = [];
  const head = [p.docNumber, p.partyName].filter(Boolean).join(' — ');
  if (head) bits.push(head);
  if (p.amount && p.currencyCode) {
    bits.push(formatMoney(p.amount, { locale, currency: p.currencyCode }));
  }
  if (n.eventType === 'store.order.placed' && p.source) {
    // Three doors, not two: a rep's order used to be labelled "Website".
    bits.push(p.source === 'app' ? t('notifications.sourceApp')
      : p.source === 'mandob' ? t('notifications.sourceRep')
        : t('notifications.sourceWeb'));
  }
  // Who was on the counter — the one fact a till sale has that a register sale doesn't.
  if (n.eventType === 'store.order.counter' && p.repName) bits.push(p.repName);
  return bits.join(' · ');
}

/** Add/remove the "(N) " unread prefix. Derives the base from the CURRENT title
 *  each time (no cached copy) so it always respects whatever set the tab title —
 *  e.g. the store name set from the tenant — instead of pinning a stale value. */
function applyTitleBadge(unread: number): void {
  if (typeof document === 'undefined') return;
  const base = document.title.replace(/^\(\d+\+?\)\s/, '');
  document.title = unread > 0 ? `(${unread > 99 ? '99+' : unread}) ${base}` : base;
}

export function NotificationsBell(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language;
  const [items, setItems] = useState<StaffNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [muted, setMuted] = useState<boolean>(() => isNotifySoundMuted());
  // Previous poll snapshot — lets us tell "something NEW arrived" (count rose
  // AND the newest id changed) apart from the first load, which must be silent.
  const prevRef = useRef<{ count: number; newestId: string | null } | null>(null);

  const markAllSeen = useCallback(async (): Promise<void> => {
    setUnread(0);
    setItems((xs) => xs.map((x) => ({ ...x, unread: false })));
    prevRef.current = prevRef.current ? { ...prevRef.current, count: 0 } : { count: 0, newestId: null };
    try {
      await api().post('/notifications/mark-all-seen', {});
    } catch {
      /* next poll re-syncs */
    }
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const r = await api().get<StaffNotificationList>('/notifications', { params: { limit: 20 } });
      const { items: fresh, unreadCount } = r.data;
      const newestId = fresh[0]?.id ?? null;
      const prev = prevRef.current;
      if (prev !== null && unreadCount > prev.count && newestId !== null && newestId !== prev.newestId) {
        const newest = fresh[0]!;
        toast(titleOf(t, newest), {
          description: bodyOf(t, locale, newest),
          ...(newest.linkPath
            ? {
                action: {
                  label: t('notifications.view'),
                  onClick: () => { void markAllSeen(); navigate(newest.linkPath!); },
                },
              }
            : {}),
        });
        playNotificationSound();
      }
      prevRef.current = { count: unreadCount, newestId };
      setItems(fresh);
      setUnread(unreadCount);
    } catch {
      /* transient poll failure — keep the last state */
    }
  }, [t, locale, navigate, markAllSeen]);

  // Poll while the tab is visible; refetch immediately on show/focus.
  useEffect(() => {
    void refresh();
    const tick = (): void => {
      if (!document.hidden) void refresh();
    };
    const id = window.setInterval(tick, POLL_MS);
    const onVisible = (): void => {
      if (!document.hidden) void refresh();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // Tab-title unread prefix "(N) ".
  useEffect(() => {
    applyTitleBadge(unread);
    return () => applyTitleBadge(0);
  }, [unread]);

  const onOpen = useCallback((n: StaffNotification): void => {
    void markAllSeen();
    if (n.linkPath) navigate(n.linkPath);
  }, [markAllSeen, navigate]);

  const toggleMute = useCallback((): void => {
    setMuted((m) => {
      setNotifySoundMuted(!m);
      return !m;
    });
  }, []);

  const iraqTime = useCallback((iso: string): string => {
    try {
      return formatDateTime(iso, { locale, timeZone: 'Asia/Baghdad' });
    } catch {
      return iso;
    }
  }, [locale]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="icon-btn relative"
          aria-label={t('notifications.aria')}
          title={t('notifications.title')}
        >
          {unread > 0 ? <BellRing aria-hidden="true" /> : <Bell aria-hidden="true" />}
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute -end-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[22rem] p-0">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <span className="text-sm font-semibold text-fg">{t('notifications.title')}</span>
          <span className="flex items-center gap-1">
            <button
              type="button"
              className="rounded-md p-1.5 text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg"
              onClick={toggleMute}
              aria-label={muted ? t('notifications.unmuteSound') : t('notifications.muteSound')}
              title={muted ? t('notifications.unmuteSound') : t('notifications.muteSound')}
            >
              {muted ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
            </button>
            {unread > 0 && (
              <button
                type="button"
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand transition-colors hover:bg-bg-muted"
                onClick={() => void markAllSeen()}
              >
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                {t('notifications.markAllRead')}
              </button>
            )}
          </span>
        </div>
        <DropdownMenuSeparator className="my-0" />
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <Bell className="h-6 w-6 text-fg-muted/60" aria-hidden="true" />
            <p className="text-sm text-fg-muted">{t('notifications.empty')}</p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto py-1">
            {items.map((n) => {
              const Icon = EVENT_ICONS[n.eventType] ?? (n.payload.isCreditNote ? Undo2 : Bell);
              return (
                <DropdownMenuItem
                  key={n.id}
                  onSelect={() => onOpen(n)}
                  className="cursor-pointer items-start gap-3 px-3 py-2.5"
                >
                  <span
                    className={cn(
                      'mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full',
                      n.unread ? 'bg-brand/15 text-brand' : 'bg-bg-muted text-fg-muted',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={cn('truncate text-sm', n.unread ? 'font-semibold text-fg' : 'text-fg')}>
                        {titleOf(t, n)}
                      </span>
                      {n.unread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />}
                    </span>
                    {bodyOf(t, locale, n) && (
                      <span className="mt-0.5 block truncate text-xs text-fg-muted">{bodyOf(t, locale, n)}</span>
                    )}
                    <span className="mt-0.5 block text-[11px] text-fg-muted/80" title={iraqTime(n.createdAt)}>
                      {formatRelative(n.createdAt, { locale })}
                    </span>
                  </span>
                </DropdownMenuItem>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
